import { writable, derived, get } from 'svelte/store';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Database, Tables } from '../integrations/supabase/types';

type XPRewardRow = Tables<'xp_rewards'>;
type XPTransactionRow = Tables<'xp_transactions'>;

const supabaseClient: SupabaseClient<Database> = supabase;

export interface XPReward {
  reward_type: string;
  base_value: number;
  multiplier: number;
  description: string | null;
}

export interface XPState {
  total_xp: number;
  current_level: number;
  level_xp_start: number;
  level_xp_end: number;
  xp_to_next_level: number;
  level_progress_percentage: number;
}

export interface XPTransaction {
  id: string;
  reward_type: string;
  base_value: number;
  multiplier: number;
  xp_earned: number;
  description: string | null;
  created_at: string;
}

const defaultXPState: XPState = {
  total_xp: 0,
  current_level: 1,
  level_xp_start: 0,
  level_xp_end: 100,
  xp_to_next_level: 100,
  level_progress_percentage: 0,
};

// Stores
export const xpState = writable<XPState>({ ...defaultXPState });
export const xpRewards = writable<Map<string, XPReward>>(new Map());
export const recentXPTransactions = writable<XPTransaction[]>([]);
export const isLoadingXP = writable(false);

// Derived stores
export const currentLevelProgress = derived(xpState, ($xpState) => {
  return $xpState.level_progress_percentage;
});

// Calculate level and XP ranges from total XP
function calculateLevelInfo(totalXP: number): Omit<XPState, 'total_xp'> {
  let level = 1;
  let levelStart = 0;
  let levelEnd = 100;

  // Level N requires: 100 * (N-1) * N / 2 total XP
  while (totalXP >= levelEnd) {
    level++;
    levelStart = levelEnd;
    levelEnd = (100 * level * (level + 1)) / 2;
  }

  const xpToNextLevel = levelEnd - totalXP;
  const xpInCurrentLevel = totalXP - levelStart;
  const xpNeededForLevel = levelEnd - levelStart;
  const progressPercentage = Math.round((xpInCurrentLevel / xpNeededForLevel) * 100);

  return {
    current_level: level,
    level_xp_start: levelStart,
    level_xp_end: levelEnd,
    xp_to_next_level: xpToNextLevel,
    level_progress_percentage: progressPercentage,
  };
}

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabaseClient.auth.getUser();
  return user?.id ?? null;
}

export const xpActions = {
  // Load XP rewards configuration from database
  async loadRewards() {
    try {
      const { data, error } = await supabaseClient
        .from('xp_rewards')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const rewardsMap = new Map<string, XPReward>();
      ((data as XPRewardRow[]) || []).forEach((reward) => {
        rewardsMap.set(reward.reward_type, {
          reward_type: reward.reward_type,
          base_value: reward.base_value ?? 0,
          multiplier: reward.multiplier ?? 1.0,
          description: reward.description,
        });
      });

      // Ensure test rewards exist - if not, try to create them
      const testRewardTypes = ['test_short', 'test_medium', 'test_long'];
      const missingTestRewards = testRewardTypes.filter((type) => !rewardsMap.has(type));

      if (missingTestRewards.length > 0) {
        console.warn('Missing test rewards, attempting to create them:', missingTestRewards);
        await xpActions.ensureTestRewards();
        // Reload after creating
        const { data: refreshedData } = await supabaseClient
          .from('xp_rewards')
          .select('*')
          .eq('is_active', true);

        if (refreshedData) {
          rewardsMap.clear();
          (refreshedData as XPRewardRow[]).forEach((reward) => {
            rewardsMap.set(reward.reward_type, {
              reward_type: reward.reward_type,
              base_value: reward.base_value ?? 0,
              multiplier: reward.multiplier ?? 1.0,
              description: reward.description,
            });
          });
        }
      }

      xpRewards.set(rewardsMap);
    } catch (error) {
      console.error('Error loading XP rewards:', error);
    }
  },

  // Ensure test XP rewards exist in database
  async ensureTestRewards() {
    try {
      const testRewards = [
        {
          reward_type: 'test_short',
          base_value: 5,
          multiplier: 1.0,
          description: 'Krátký test (10 otázek) - max XP při 100%',
          is_active: true,
        },
        {
          reward_type: 'test_medium',
          base_value: 7,
          multiplier: 1.0,
          description: 'Střední test (20 otázek) - max XP při 100%',
          is_active: true,
        },
        {
          reward_type: 'test_long',
          base_value: 10,
          multiplier: 1.0,
          description: 'Dlouhý test (50 otázek) - max XP při 100%',
          is_active: true,
        },
      ];

      // Try to insert test rewards (will fail silently if they already exist due to unique constraint)
      for (const reward of testRewards) {
        const { error } = await supabaseClient.from('xp_rewards').upsert(reward, {
          onConflict: 'reward_type',
          ignoreDuplicates: false,
        });

        if (error && !error.message.includes('duplicate key')) {
          console.error('Error creating test reward:', reward.reward_type, error);
        }
      }

      console.log('✅ Test rewards ensured');
    } catch (error) {
      console.error('Error ensuring test rewards:', error);
    }
  },

  // Load user XP state
  async loadXPState() {
    isLoadingXP.set(true);
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        xpState.set({ ...defaultXPState });
        return;
      }

      const { data, error } = await supabaseClient
        .from('user_progress')
        .select('total_xp, current_level, level_xp_start')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      const totalXP = data?.total_xp ?? 0;
      const levelInfo = calculateLevelInfo(totalXP);

      xpState.set({
        total_xp: totalXP,
        ...levelInfo,
      });
    } catch (error) {
      console.error('Error loading XP state:', error);
      xpState.set({ ...defaultXPState });
    } finally {
      isLoadingXP.set(false);
    }
  },

  // Load recent XP transactions
  async loadRecentTransactions(limit = 10) {
    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        recentXPTransactions.set([]);
        return;
      }

      const { data, error } = await supabaseClient
        .from('xp_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const transactions: XPTransaction[] = ((data as XPTransactionRow[]) || []).map((tx) => ({
        id: tx.id,
        reward_type: tx.reward_type,
        base_value: tx.base_value ?? 0,
        multiplier: tx.multiplier ?? 1.0,
        xp_earned: tx.xp_earned ?? 0,
        description: tx.description,
        created_at: tx.created_at ?? new Date().toISOString(),
      }));

      recentXPTransactions.set(transactions);
    } catch (error) {
      console.error('Error loading XP transactions:', error);
      recentXPTransactions.set([]);
    }
  },

  // Award XP to user
  async awardXP(
    rewardType: string,
    metadata?: Record<string, any>,
    customDescription?: string
  ): Promise<number> {
    try {
      const userId = await getCurrentUserId();
      if (!userId) return 0;

      // Get reward configuration
      const rewards = get(xpRewards);
      const reward = rewards.get(rewardType);

      if (!reward) {
        console.warn(`XP reward type "${rewardType}" not found in configuration`);
        return 0;
      }

      const xpEarned = Math.round(reward.base_value * reward.multiplier);

      // Record transaction
      const { error: txError } = await supabaseClient.from('xp_transactions').insert({
        user_id: userId,
        reward_type: rewardType,
        base_value: reward.base_value,
        multiplier: reward.multiplier,
        xp_earned: xpEarned,
        description: customDescription || reward.description,
        metadata: metadata || {},
      });

      if (txError) throw txError;

      // Update user total XP
      const currentState = get(xpState);
      const newTotalXP = currentState.total_xp + xpEarned;
      const levelInfo = calculateLevelInfo(newTotalXP);
      const previousLevel = currentState.current_level;
      const newLevel = levelInfo.current_level;

      // Use upsert to create user_progress if it doesn't exist
      const { error: updateError } = await supabaseClient.from('user_progress').upsert(
        {
          user_id: userId,
          total_xp: newTotalXP,
          current_level: newLevel,
          level_xp_start: levelInfo.level_xp_start,
        },
        {
          onConflict: 'user_id',
        }
      );

      if (updateError) throw updateError;

      // Update local state
      xpState.set({
        total_xp: newTotalXP,
        ...levelInfo,
      });

      // Check for level up
      if (newLevel > previousLevel) {
        await xpActions.handleLevelUp(previousLevel, newLevel);
      }

      // Reload recent transactions
      await xpActions.loadRecentTransactions();

      return xpEarned;
    } catch (error) {
      console.error('Error awarding XP:', error);
      return 0;
    }
  },

  // Handle level up event
  async handleLevelUp(oldLevel: number, newLevel: number) {
    console.log(`🎉 Level up! ${oldLevel} → ${newLevel}`);

    // Check if user reached a new league and award XP for league promotion
    const leagueThresholds = [
      { level: 3, rewardType: 'league_bronze' },
      { level: 7, rewardType: 'league_silver' },
      { level: 14, rewardType: 'league_gold' },
      { level: 30, rewardType: 'league_platinum' },
      { level: 50, rewardType: 'league_diamond' },
    ];

    for (const { level, rewardType } of leagueThresholds) {
      if (oldLevel < level && newLevel >= level) {
        // Award league promotion XP (but don't trigger recursive level up)
        const userId = await getCurrentUserId();
        if (!userId) return;

        const rewards = get(xpRewards);
        const reward = rewards.get(rewardType);
        if (!reward) continue;

        const xpEarned = Math.round(reward.base_value * reward.multiplier);

        // Record transaction only
        await supabaseClient.from('xp_transactions').insert({
          user_id: userId,
          reward_type: rewardType,
          base_value: reward.base_value,
          multiplier: reward.multiplier,
          xp_earned: xpEarned,
          description: reward.description,
        });
      }
    }
  },

  // Award XP for test completion (scaled by score)
  async awardTestXP(testType: 'short' | 'medium' | 'long', scorePercentage: number) {
    const rewardTypeMap = {
      short: 'test_short',
      medium: 'test_medium',
      long: 'test_long',
    };

    const rewardType = rewardTypeMap[testType];
    let rewards = get(xpRewards);

    // Ensure rewards are loaded
    if (rewards.size === 0) {
      console.log('XP rewards not loaded, loading now...');
      await xpActions.loadRewards();
      rewards = get(xpRewards);
    }

    let reward = rewards.get(rewardType);

    // If still no reward, create default test rewards
    if (!reward) {
      console.warn(`Test reward "${rewardType}" not found, creating defaults...`);
      await xpActions.ensureTestRewards();
      await xpActions.loadRewards();
      rewards = get(xpRewards);
      reward = rewards.get(rewardType);
    }

    if (!reward) {
      console.error(`Failed to get or create reward for ${rewardType}`);
      return 0;
    }

    // Calculate XP based on score (linear scale)
    // 100% = full XP, 10% = 10% of XP, 0% = 0 XP
    const scaledBaseValue = Math.round((reward.base_value * scorePercentage) / 100);
    const xpEarned = Math.round(scaledBaseValue * reward.multiplier);

    // Manually record with scaled value
    const userId = await getCurrentUserId();
    if (!userId) return 0;

    try {
      // Record transaction
      const { error: txError } = await supabaseClient.from('xp_transactions').insert({
        user_id: userId,
        reward_type: rewardType,
        base_value: scaledBaseValue,
        multiplier: reward.multiplier,
        xp_earned: xpEarned,
        description: `${reward.description} (${scorePercentage}% úspěšnost)`,
        metadata: { scorePercentage, testType },
      });

      if (txError) throw txError;

      // Update total XP
      const currentState = get(xpState);
      const newTotalXP = currentState.total_xp + xpEarned;
      const levelInfo = calculateLevelInfo(newTotalXP);

      // Use upsert to create user_progress if it doesn't exist
      const { error: updateError } = await supabaseClient.from('user_progress').upsert(
        {
          user_id: userId,
          total_xp: newTotalXP,
          current_level: levelInfo.current_level,
          level_xp_start: levelInfo.level_xp_start,
        },
        {
          onConflict: 'user_id',
        }
      );

      if (updateError) throw updateError;

      xpState.set({
        total_xp: newTotalXP,
        ...levelInfo,
      });

      await xpActions.loadRecentTransactions();

      console.log(
        `✅ Test XP awarded: ${xpEarned} XP for ${testType} test with ${scorePercentage}% score`
      );

      return xpEarned;
    } catch (error) {
      console.error('Error awarding test XP:', error);
      return 0;
    }
  },
};
